import { useEffect, useState } from 'react';

import { Button, SwitchCheck } from '@opositandopn/ui';
import { IoReaderOutline } from 'react-icons/io5';

import TheoryImg from '../../../assets/images/719981ee-59ba-4246-8ae6-2806079b9df0.webp';
import { BackdropDialog } from '../../../components/shared/backdrop/BackdropDialog';
import { SectionCard } from '../../../components/shared/section-card/SectionCard';
import {
  APTITUDE_SUBJECT_ID,
  ENGLISH_SUBJECT_ID,
  THEORY_SUBJECT_ID,
} from '../../../constants/subjects';
import {
  CreateMultipleChoiceQuestionAttemptCommandResult,
  CreateRandomMultipleChoiceQuestionAttemptRequest,
  DifficultyLevel,
  useCreateRandomMultipleChoiceQuestionAttempt,
} from '../../../features/questions/api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionAttempt } from '../../../features/questions/components/multiple-choice-question-attempt/MultipleChoiceQuestionAttempt';
import { SubjectSelectableList } from '../../../features/subjects/components/subject-selectable-list/SubjectSelectableList';
import { BaseComponentProps } from '../../../types/base-component.types';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  difficultyLevels?: DifficultyLevel[];
};

function MultipleChoiceQuestionAttemptCard({
  className,
  subjectId,
  difficultyLevels,
  testId,
}: ContainerProps) {
  const [showLessons, setShowLessons] = useState(false);
  const [countdownActive, setCountdownActive] = useState(false);
  const [showAttempt, setShowAttempt] = useState(false);
  const [attempt, setAttempt] =
    useState<CreateMultipleChoiceQuestionAttemptCommandResult>();
  const [attemptFilter, setAttemptFilter] = useState<{
    subjectId: string;
    numberOfChoices?: number;
    unitIds?: string[];
    lessonIds?: string[];
    difficultyLevels?: DifficultyLevel[];
  }>({
    subjectId: subjectId,
    difficultyLevels: difficultyLevels,
  });

  useEffect(() => {
    setAttemptFilter((prev) => ({
      ...prev,
      subjectId: subjectId,
      difficultyLevels: difficultyLevels,
    }));
  }, [subjectId, difficultyLevels]);

  const { mutate: createRandomMultipleChoiceQuestionAttempt } =
    useCreateRandomMultipleChoiceQuestionAttempt();

  const createAttempt = (
    request: CreateRandomMultipleChoiceQuestionAttemptRequest,
  ) => {
    // Open the attempt dialog straight away so its loading state covers the
    // wait for the API call instead of leaving the user without feedback.
    setShowAttempt(true);
    setAttempt(undefined);
    createRandomMultipleChoiceQuestionAttempt(request, {
      onSuccess: (response) => {
        setAttempt(response);
      },
      onError: () => {
        setAttempt(undefined);
        setShowAttempt(false);
        setShowLessons(true);
      },
    });
  };

  return (
    <>
      <SectionCard
        title={
          subjectId === THEORY_SUBJECT_ID
            ? 'Preguntas de teoría'
            : subjectId === APTITUDE_SUBJECT_ID
              ? 'Preguntas de psicotécnicos'
              : subjectId === ENGLISH_SUBJECT_ID
                ? 'Preguntas de inglés'
                : undefined
        }
        image={TheoryImg}
        tooltip={
          subjectId === THEORY_SUBJECT_ID
            ? 'Responde preguntas sueltas con explicación al responder. Más de 25.000 preguntas disponibles.'
            : subjectId === APTITUDE_SUBJECT_ID
              ? 'Estos test evaluarán tus capacidades y aptitudes intelectuales y profesionales de distinto tipo, como el cálculo mental, la memoria verbal y visual o la visión espacial.'
              : subjectId === ENGLISH_SUBJECT_ID
                ? 'Estos test evaluarán tu nivel de inglés con preguntas específicas sobre comprensión de texto y gramática inglesa.'
                : undefined
        }
        button={
          <Button
            variant="outline-primary"
            onClick={() => {
              setShowLessons(true);
            }}
            suffixIcon={<IoReaderOutline />}
          >
            Preguntas sueltas
          </Button>
        }
        className={className}
        testId={testId}
      >
        <SwitchCheck
          label="Cronómetro por pregunta"
          checked={countdownActive}
          onChange={() => {
            setCountdownActive((prev) => !prev);
          }}
        />
      </SectionCard>
      <SubjectSelectableList
        open={showLessons}
        subjectId={subjectId}
        onSelect={(ids) => {
          setAttemptFilter((prev) => ({
            ...prev,
            lessonIds: ids.map((id) => id.toString()),
          }));

          setShowLessons(false);
          createAttempt({
            ...attemptFilter,
            lessonIds: ids.map((id) => id.toString()),
          });
        }}
        onClose={() => {
          setShowLessons(false);
        }}
      />
      <BackdropDialog open={showAttempt}>
        <MultipleChoiceQuestionAttempt
          countdown={countdownActive}
          attempt={attempt}
          onNext={() => {
            createAttempt(attemptFilter);
          }}
          onClose={() => {
            setShowAttempt(false);
          }}
        />
      </BackdropDialog>
    </>
  );
}

export { MultipleChoiceQuestionAttemptCard };
